import { locateProxy } from '#~/agent/index.js';
import type { OmitSyncProps } from '#~/api/types.js';
import { StudioError } from '#~/error/index.js';
// Gating (spec §6): NDI output over a transitory placeholder is forbidden
// until auth. Per `reference-rules-in-comments` (ai.knowledge/global).
import { isTransitoryId } from '#~/device/gating.js';
import type { Device } from '@studio/models/device';
import type { ControlDevice } from '@tryiris-ai/common.protojs/agent/v1/agent';

/** @internal */
export const setNDIDeviceStatus = async (
  deviceId: Device['id'],
  {
    enable,
    video,
    audio
  }: {
    enable?: boolean;
    video?: boolean;
    audio?: boolean;
  }
): Promise<void> => {
  // Gating §6 — NDI capability over a transitory placeholder. Defensive
  // bail so NDI is a clean no-op (the `!proxy` branch below would throw
  // 'PW-79B' for a placeholder, which has no proxy).
  if (isTransitoryId(deviceId)) {
    return;
  }

  const proxy = locateProxy(deviceId);

  if (!proxy) {
    throw new Error('PW-79B');
  }

  const studioDevice = proxy.state.control?.studio?.device?.[deviceId];
  const deviceName = studioDevice?.name ?? '';

  const config: OmitSyncProps<Pick<ControlDevice, 'egress'>> = {
    egress: {
      ndiOutput:
        enable !== undefined
          ? {
              enable: enable,
              sinkName: deviceName,
              uptimeSeconds: undefined,
              includeAudio: undefined,
              audio: audio,
              video: video
            }
          : undefined,
      recording: undefined,
      rtmp: undefined
    }
  };

  // If the user doesn't set a sink name, we'll use the default source name.
  const mutation = {
    control: {
      device: {
        [deviceId]: config
      }
    }
  };

  try {
    await proxy.mutate(mutation, {});
  } catch (cause) {
    throw new StudioError('Unable to egress video.', { cause });
  }
};
